import React, { useEffect, useState } from 'react';
import lmsApi from '../../services/api';
import { formatDateTime } from '../../utils/dateFormatter';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Search, ArrowUp, ArrowDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '@/components/ui/input';
import StaffLayout from '../../components/StaffLayout';

// Types (Ideally move to a types file)
interface Transaction {
    id: string;
    transaction_type: string;
    amount: string | number;
    status: string;
    description: string;
    transaction_date: string;
}

interface Loan {
    id: number;
    loan_application_number: string;
    customer_info?: {
        phone_number: string;
    };
    loan_amount: string | number;
    current_outstanding: string | number;
    status: string;
    tag: string;
    days_past_due: number;
    transactions?: Transaction[];
}

const StaffLoanRow = ({ loan, getTagColor }: { loan: Loan, getTagColor: (tag: string) => string }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <>
            <TableRow
                className="cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <TableCell className="font-medium">{loan.loan_application_number}</TableCell>
                <TableCell>{loan.customer_info?.phone_number || 'N/A'}</TableCell>
                <TableCell>₹{parseFloat(String(loan.loan_amount)).toFixed(2)}</TableCell>
                <TableCell>₹{parseFloat(String(loan.current_outstanding)).toFixed(2)}</TableCell>
                <TableCell>
                    <Badge className={
                        loan.status === 'ACTIVE' ? 'bg-green-100 text-green-800 hover:bg-green-100 border-green-200' :
                            loan.status === 'APPLIED' ? 'bg-blue-100 text-blue-800 hover:bg-blue-100 border-blue-200' :
                                ['DEFAULTED', 'NPA'].includes(loan.status) ? 'bg-red-100 text-red-800 hover:bg-red-100 border-red-200' :
                                    'bg-gray-100 text-gray-800 hover:bg-gray-100 border-gray-200'
                    }>
                        {loan.status}
                    </Badge>
                </TableCell>
                <TableCell>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${getTagColor(loan.tag)}`}>
                        {loan.tag}
                    </span>
                </TableCell>
                <TableCell>{loan.days_past_due}</TableCell>
                <TableCell>
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </TableCell>
            </TableRow>
            <AnimatePresence>
                {isExpanded && (
                    <TableRow>
                        <TableCell colSpan={8} className="p-0 border-b-0">
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3, ease: "easeInOut" }}
                                className="overflow-hidden bg-gray-50"
                            >
                                <div className="p-4 border-t border-gray-200 shadow-inner">
                                    <div className="flex justify-between items-center mb-4">
                                        <h4 className="font-semibold text-lg text-gray-800">Transaction History</h4>
                                        <Badge variant="secondary">Total Transactions: {loan.transactions?.length || 0}</Badge>
                                    </div>

                                    {loan.transactions && loan.transactions.length > 0 ? (
                                        <div className="bg-white rounded-md border overflow-hidden">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow className="bg-gray-100">
                                                        <TableHead>Date</TableHead>
                                                        <TableHead>Type</TableHead>
                                                        <TableHead>Amount</TableHead>
                                                        <TableHead>Status</TableHead>
                                                        <TableHead>Description</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {loan.transactions.map((txn) => (
                                                        <TableRow key={txn.id}>
                                                            <TableCell>{formatDateTime(txn.transaction_date)}</TableCell>
                                                            <TableCell className={txn.transaction_type === 'CREDIT' ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                                                                {txn.transaction_type}
                                                            </TableCell>
                                                            <TableCell>₹{parseFloat(String(txn.amount)).toFixed(2)}</TableCell>
                                                            <TableCell>
                                                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${txn.status === 'SUCCESS' ? 'bg-green-100 text-green-800' :
                                                                    txn.status === 'PENDING' ? 'bg-amber-100 text-amber-800' :
                                                                        'bg-red-100 text-red-800'
                                                                    }`}>
                                                                    {txn.status}
                                                                </span>
                                                            </TableCell>
                                                            <TableCell className="text-gray-500 text-sm">{txn.description || '-'}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-gray-500 italic bg-white rounded border border-dashed">
                                            No transaction history available.
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        </TableCell>
                    </TableRow>
                )}
            </AnimatePresence>
        </>
    );
};

const LMSStaffDashboard = () => {
    const [loans, setLoans] = useState<Loan[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'loan_application_number', direction: 'asc' });

    const fetchLoans = async () => {
        setLoading(true);
        try {
            const params = filterStatus ? { status: filterStatus } : {};
            const response = await lmsApi.post('/staff/loans/list_post/', params);
            setLoans(response.data);
        } catch (error) {
            console.error(error);
            toast.error("Failed to fetch staff loans");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLoans();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterStatus]);

    const getTagColor = (tag: string) => {
        if (tag === 'Critical' || tag === 'High Risk') return 'bg-red-100 text-red-800';
        if (tag === 'On Time') return 'bg-green-100 text-green-800';
        return 'bg-gray-100 text-gray-800';
    };

    const handleSort = (key: string) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const filteredAndSortedLoans = React.useMemo(() => {
        let result = [...loans];

        // Search Filter
        if (searchTerm) {
            const lowSearch = searchTerm.toLowerCase();
            result = result.filter(l =>
                l.loan_application_number.toLowerCase().includes(lowSearch) ||
                l.customer_info?.phone_number?.toLowerCase().includes(lowSearch)
            );
        }

        // Sorting
        result.sort((a: any, b: any) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            const numericFields = ['loan_amount', 'current_outstanding', 'days_past_due'];
            if (numericFields.includes(sortConfig.key)) {
                aValue = parseFloat(aValue) || 0;
                bValue = parseFloat(bValue) || 0;
            }

            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return result;
    }, [loans, searchTerm, sortConfig]);

    return (
        <StaffLayout title="Staff Portal" userName="Staff Administrator">
            <Card className="bg-white shadow-xl rounded-lg overflow-hidden border-0 ring-1 ring-gray-200">
                <div className="p-6 border-b border-gray-100 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white">
                    <div className="relative w-full md:w-72">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                            placeholder="Search App No or Customer..."
                            className="pl-10"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex gap-2 w-full md:w-auto overflow-x-auto">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setFilterStatus('')}
                            className={!filterStatus ? 'bg-white text-black' : ''}
                        >
                            All
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setFilterStatus('NPA')}
                            className={filterStatus === 'NPA' ? 'bg-white text-black' : ''}
                        >
                            NPA
                        </Button>
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setFilterStatus('DEFAULTED')}
                            className={filterStatus === 'DEFAULTED' ? 'bg-white text-black' : ''}
                        >
                            Defaulted
                        </Button>
                    </div>
                </div>

                <div className="p-6">
                    {loading ? (
                        <div className="text-center py-4">Loading data...</div>
                    ) : (
                        <Table>
                            <TableCaption>List of all loans in the system.</TableCaption>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="cursor-pointer" onClick={() => handleSort('loan_application_number')}>
                                        <div className="flex items-center gap-1">
                                            App No
                                            {sortConfig.key === 'loan_application_number' && (sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                                        </div>
                                    </TableHead>
                                    <TableHead className="cursor-pointer" onClick={() => handleSort('customer')}>
                                        <div className="flex items-center gap-1">
                                            Customer
                                            {sortConfig.key === 'customer' && (sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                                        </div>
                                    </TableHead>
                                    <TableHead className="cursor-pointer" onClick={() => handleSort('loan_amount')}>
                                        <div className="flex items-center gap-1">
                                            Amount
                                            {sortConfig.key === 'loan_amount' && (sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                                        </div>
                                    </TableHead>
                                    <TableHead className="cursor-pointer" onClick={() => handleSort('current_outstanding')}>
                                        <div className="flex items-center gap-1">
                                            Outstanding
                                            {sortConfig.key === 'current_outstanding' && (sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                                        </div>
                                    </TableHead>
                                    <TableHead className="cursor-pointer" onClick={() => handleSort('status')}>Status</TableHead>
                                    <TableHead className="cursor-pointer" onClick={() => handleSort('tag')}>Risk Tag</TableHead>
                                    <TableHead className="cursor-pointer" onClick={() => handleSort('days_past_due')}>
                                        <div className="flex items-center gap-1">
                                            DPD
                                            {sortConfig.key === 'days_past_due' && (sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />)}
                                        </div>
                                    </TableHead>
                                    <TableHead></TableHead> {/* Chevron Column */}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredAndSortedLoans.map((loan) => (
                                    <StaffLoanRow key={loan.id} loan={loan} getTagColor={getTagColor} />
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </Card>
        </StaffLayout>
    );
};

export default LMSStaffDashboard;
